import apiClient from './client';

export interface Job {
  id: number;
  jobNumber: string;
  title: string;
  client: string;
  clientId?: number;
  status: string;
  crew: string;
  startDate: string;
  estimatedEndDate?: string;
}

export interface JobsResponse {
  jobs: Job[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export const getJobs = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  crew?: string;
  clientId?: string;
}): Promise<JobsResponse> => {
  const response = await apiClient.get('/jobs', { params });
  return response.data.data;
};

export const getJobById = async (id: string): Promise<Record<string, unknown>> => {
  const response = await apiClient.get(`/jobs/${id}`);
  return response.data.data;
};

export const createJob = async (jobData: Record<string, unknown>): Promise<Record<string, unknown>> => {
  const response = await apiClient.post('/jobs', jobData);
  return response.data.data;
};

export const updateJob = async (id: string, jobData: Record<string, unknown>): Promise<void> => {
  await apiClient.put(`/jobs/${id}`, jobData);
};

export const deleteJob = async (id: string): Promise<void> => {
  await apiClient.delete(`/jobs/${id}`);
};

export const duplicateJob = async (id: string): Promise<Record<string, unknown>> => {
  const response = await apiClient.post(`/jobs/${id}/duplicate`);
  return response.data.data;
};

export const updateJobStatus = async (id: string, status: string, userId?: string, notes?: string): Promise<void> => {
  await apiClient.put(`/jobs/${id}/status`, { status, userId, notes });
};

export interface JobFinancials {
  labourCost?: number;
  labourHours?: number;
  labourByWorker?: { name: string; hours: number; rate: number; cost: number }[];
  budget: number;
  approvedChangeOrderTotal: number;
  approvedChangeOrderCount: number;
  revisedContractValue: number;
  spent: number;
  actualCost?: number;
  expensesTotal?: number;
  remaining: number;
  percentageUsed: number;
  profitMargin: number;
}

export const getJobFinancials = async (id: string): Promise<JobFinancials> => {
  const response = await apiClient.get(`/jobs/${id}/financials`);
  return response.data.data;
};

export const getJobNotes = async (id: string): Promise<Record<string, unknown>[]> => {
  const response = await apiClient.get(`/jobs/${id}/notes`);
  return response.data.data;
};

export const createJobNote = async (
  id: string, 
  note: string, 
  userId?: string,
  noteType?: 'general' | 'important' | 'issue' | 'resolved',
  isPinned?: boolean
): Promise<Record<string, unknown>> => {
  const response = await apiClient.post(`/jobs/${id}/notes`, { 
    note, 
    user_id: userId,
    note_type: noteType || 'general',
    is_pinned: isPinned || false
  });
  return response.data.data;
};

export const deleteJobNote = async (id: string, noteId: string): Promise<void> => {
  await apiClient.delete(`/jobs/${id}/notes/${noteId}`);
};

export interface JobDocument {
  id: number;
  /** Display name: the label when set, else the original filename. */
  name: string;
  /** The file's original (vendor-assigned) filename — kept for back-reference. */
  originalName?: string;
  label?: string | null;
  type: string;
  size: number;
  url: string;
  uploadedAt: string;
  /** Document firewall: portal visibility flags. */
  visibleToEmployee?: boolean;
  visibleToClient?: boolean;
  /** Review flags: how many workers are flagged / how many have read it. */
  flagCount?: number;
  flagReadCount?: number;
}

export interface DocumentFlag {
  id: number;
  user_id: number;
  name: string;
  note?: string | null;
  created_at: string;
  read_at: string | null;
}

export const getJobDocuments = async (id: string): Promise<JobDocument[]> => {
  const response = await apiClient.get(`/jobs/${id}/documents`);
  return response.data.data;
};

export const uploadJobDocument = async (id: string, file: File): Promise<JobDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`/jobs/${id}/documents`, formData, {
    transformRequest: [(data, headers) => {
      headers.delete('Content-Type');
      return data;
    }],
  });
  return response.data.data;
};

export const scanJobDocument = async (id: string): Promise<JobDocument> => {
  const response = await apiClient.post(`/jobs/${id}/documents/scan`);
  return response.data.data;
};

export const deleteJobDocument = async (id: string, docId: string): Promise<void> => {
  await apiClient.delete(`/jobs/${id}/documents/${docId}`);
};

export const getJobStatusHistory = async (id: string): Promise<Record<string, unknown>[]> => {
  const response = await apiClient.get(`/jobs/${id}/status-history`);
  return response.data.data;
};

export const getJobMaterials = async (id: string): Promise<Record<string, unknown>[]> => {
  const response = await apiClient.get(`/jobs/${id}/materials`);
  return response.data.data;
};

export const addJobMaterial = async (id: string, material: Record<string, unknown>): Promise<Record<string, unknown>> => {
  const response = await apiClient.post(`/jobs/${id}/materials`, material);
  return response.data.data;
};

export const getJobTeam = async (id: string): Promise<Record<string, unknown>[]> => {
  const response = await apiClient.get(`/jobs/${id}/team`);
  return response.data.data;
};

export const addJobTeamMember = async (id: string, teamMember: Record<string, unknown>): Promise<Record<string, unknown>> => {
  const response = await apiClient.post(`/jobs/${id}/team`, teamMember);
  return response.data.data;
};

export const removeJobTeamMember = async (id: string, userId: string): Promise<void> => {
  await apiClient.delete(`/jobs/${id}/team/${userId}`);
};

export const updateJobNote = async (id: string, noteId: string, note: Record<string, unknown>): Promise<void> => {
  await apiClient.put(`/jobs/${id}/notes/${noteId}`, note);
};

export interface JobScheduleData {
  // Overall Job Scheduling
  jobName: string;
  jobId?: string; // Optional - auto-generated if not provided
  clientName: string;
  clientId?: string; // Optional - if provided, use existing client
  jobStartDate: string;
  jobEndDate: string;
  
  // Job Site Logistics
  siteAddress?: string;
  accessCodes?: string;
  deliveryWindows?: string;
  clientNotes?: string;
  safetyNotes?: string;
  
  // Tasks
  tasks: {
    name: string;
    description?: string;
    status: string;
    priority: 'low' | 'medium' | 'high';
    startDate: string;
    endDate: string;
    subtasks: {
      title: string;
      startDate: string;
      endDate: string;
      checklistItems: {
        text: string;
        checked: boolean;
      }[];
    }[];
  }[];
}

export const createScheduledJob = async (data: JobScheduleData): Promise<{ jobId: number; taskIds: number[] }> => {
  const response = await apiClient.post('/jobs/schedule', data);
  return response.data.data;
};

export const updateScheduledJob = async (jobId: string, data: JobScheduleData): Promise<{ jobId: number; taskIds: number[] }> => {
  const response = await apiClient.put(`/jobs/${jobId}/schedule`, data);
  return response.data.data;
};


// Document firewall: who may see an uploaded job file in the portals.
export const setJobDocumentVisibility = async (
  jobId: string | number,
  docId: number,
  flags: { visible_to_employee?: boolean; visible_to_client?: boolean },
): Promise<void> => {
  await apiClient.patch(`/jobs/${jobId}/documents/${docId}`, flags);
};

// Review flags — flag a doc for specific workers ("must read")
export const getDocumentFlags = async (jobId: string | number, docId: number): Promise<DocumentFlag[]> => {
  const { data } = await apiClient.get(`/jobs/${jobId}/documents/${docId}/flags`);
  return data.data ?? [];
};
export const addDocumentFlags = async (jobId: string | number, docId: number, userIds: number[], note?: string): Promise<void> => {
  await apiClient.post(`/jobs/${jobId}/documents/${docId}/flags`, { user_ids: userIds, note });
};
export const removeDocumentFlag = async (jobId: string | number, docId: number, userId: number): Promise<void> => {
  await apiClient.delete(`/jobs/${jobId}/documents/${docId}/flags/${userId}`);
};

// Set/clear the human display label (original filename is always retained).
export const setJobDocumentLabel = async (
  jobId: string | number,
  docId: number,
  label: string | null,
): Promise<void> => {
  await apiClient.patch(`/jobs/${jobId}/documents/${docId}`, { label });
};
